import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
  Highlighter,
  Type,
  Save,
  Eye,
  X,
  Tag,
  FolderOpen,
  Check,
  Upload,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface BlogPost {
  id?: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  featured: boolean;
  published: boolean;
  meta_title?: string;
  meta_description?: string;
  og_image_url?: string;
}

interface BlogEditorProps {
  post?: BlogPost;
  onSave: (post: BlogPost) => Promise<void>;
  onCancel: () => void;
}

const BlogEditor: React.FC<BlogEditorProps> = ({ post, onSave, onCancel }) => {
  const [formData, setFormData] = useState<BlogPost>({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    author: post?.author || '',
    category: post?.category || 'AI Technology',
    tags: post?.tags || [],
    featured: post?.featured || false,
    published: post?.published || false,
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    og_image_url: post?.og_image_url || ''
  });

  const [currentTag, setCurrentTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentHighlight, setCurrentHighlight] = useState('#FDB913');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const categories = [
    'Success Stories',
    'AI Technology',
    'Tips & Tricks',
    'Analytics',
    'Menu Strategy',
    'Business',
    'Industry News',
    'Product Updates'
  ];

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline'
        }
      }),
      Image.configure({
        inline: true,
        allowBase64: true
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Underline
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    }
  });

  useEffect(() => {
    if (formData.title && !post?.id) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, post?.id]);

  useEffect(() => {
    if (!formData.meta_title && formData.title) {
      setFormData(prev => ({ ...prev, meta_title: formData.title.slice(0, 60) }));
    }
  }, [formData.title]);

  useEffect(() => {
    if (!formData.meta_description && formData.excerpt) {
      setFormData(prev => ({ ...prev, meta_description: formData.excerpt.slice(0, 160) }));
    }
  }, [formData.excerpt]);

  const handleAddTag = () => {
    if (currentTag && !formData.tags.includes(currentTag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, currentTag] }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.slug || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      toast.success(post?.id ? 'Post updated successfully!' : 'Post created successfully!');
    } catch (error) {
      toast.error('Failed to save post');
    } finally {
      setIsSaving(false);
    }
  };

  const setLink = () => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor?.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL');
    if (url) {
      editor?.chain().focus().setImage({ src: url }).run();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setIsUploadingImage(true);
    
    // console.log('Uploading file:', {
    //   name: file.name,
    //   type: file.type,
    //   size: file.size
    // });
    
    const formData = new FormData();
    formData.append('file', file);  // Changed from 'image' to 'file'

    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        toast.error('Please login again to upload images');
        setIsUploadingImage(false);
        return;
      }
      
      const response = await fetch('/api/blog/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, og_image_url: data.url }));
        toast.success('Image uploaded successfully');
      } else {
        let errorMessage = 'Failed to upload image';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
          console.error('Upload error details:', error);
        } catch (e) {
          console.error('Failed to parse error response:', e);
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, og_image_url: '' }));
    toast.success('Image removed');
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white rounded-lg">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {post?.id ? 'Edit Blog Post' : 'Create New Blog Post'}
        </h1>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter post title..."
              className="text-xl font-semibold"
            />
          </div>

          {/* Slug */}
          <div>
            <Label htmlFor="slug">URL Slug *</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="post-url-slug"
            />
            <p className="text-sm text-gray-500 mt-1">
              URL: dineai.ai/blog/{formData.slug}
            </p>
          </div>

          {/* Excerpt */}
          <div>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              placeholder="Brief description of your post..."
              rows={3}
              maxLength={500}
            />
            <p className="text-sm text-gray-500 mt-1">
              {formData.excerpt.length}/500 characters
            </p>
          </div>

          {/* Editor Toolbar */}
          <div className="border rounded-lg">
            <div className="flex flex-wrap items-center gap-1 p-3 border-b bg-gray-50">
              {/* Text Formatting */}
              <div className="flex items-center gap-1 pr-2 border-r">
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
                  title="Underline"
                >
                  <UnderlineIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Headings */}
              <div className="flex items-center gap-1 px-2 border-r">
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}`}
                  title="Heading 1"
                >
                  <Heading1 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
                  title="Heading 2"
                >
                  <Heading2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
                  title="Heading 3"
                >
                  <Heading3 className="w-4 h-4" />
                </button>
              </div>

              {/* Lists */}
              <div className="flex items-center gap-1 px-2 border-r">
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
                  title="Numbered List"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
              </div>

              {/* Alignment */}
              <div className="flex items-center gap-1 px-2 border-r">
                <button
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
                  title="Align Left"
                >
                  <AlignLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
                  title="Align Center"
                >
                  <AlignCenter className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200' : ''}`}
                  title="Align Right"
                >
                  <AlignRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-200' : ''}`}
                  title="Justify Text"
                >
                  <AlignJustify className="w-4 h-4" />
                </button>
              </div>

              {/* Colors */}
              <div className="flex items-center gap-2 px-2 border-r">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Text:</span>
                  <input
                    type="color"
                    value={currentColor}
                    onChange={(e) => {
                      setCurrentColor(e.target.value);
                      editor.chain().focus().setColor(e.target.value).run();
                    }}
                    className="w-8 h-8 border rounded cursor-pointer"
                    title="Text Color"
                  />
                  {/* Preset colors */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => editor.chain().focus().setColor('#000000').run()}
                      className="w-6 h-6 bg-black rounded hover:ring-2 hover:ring-gray-300"
                      title="Black"
                    />
                    <button
                      onClick={() => editor.chain().focus().setColor('#1f4ed1').run()}
                      className="w-6 h-6 bg-[#1f4ed1] rounded hover:ring-2 hover:ring-gray-300"
                      title="Dine AI Blue"
                    />
                    <button
                      onClick={() => editor.chain().focus().setColor('#FDB913').run()}
                      className="w-6 h-6 bg-[#FDB913] rounded hover:ring-2 hover:ring-gray-300"
                      title="Dine AI Yellow"
                    />
                    <button
                      onClick={() => editor.chain().focus().setColor('#10b981').run()}
                      className="w-6 h-6 bg-green-500 rounded hover:ring-2 hover:ring-gray-300"
                      title="Green"
                    />
                    <button
                      onClick={() => editor.chain().focus().setColor('#ef4444').run()}
                      className="w-6 h-6 bg-red-500 rounded hover:ring-2 hover:ring-gray-300"
                      title="Red"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">BG:</span>
                  <input
                    type="color"
                    value={currentHighlight}
                    onChange={(e) => {
                      setCurrentHighlight(e.target.value);
                      editor.chain().focus().setHighlight({ color: e.target.value }).run();
                    }}
                    className="w-8 h-8 border rounded cursor-pointer"
                    title="Highlight Color"
                  />
                </div>
              </div>

              {/* Insert */}
              <div className="flex items-center gap-1 px-2 border-r">
                <button
                  onClick={setLink}
                  className="p-2 rounded hover:bg-gray-200"
                  title="Insert Link"
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={addImage}
                  className="p-2 rounded hover:bg-gray-200"
                  title="Insert Image"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('blockquote') ? 'bg-gray-200' : ''}`}
                  title="Quote"
                >
                  <Quote className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('codeBlock') ? 'bg-gray-200' : ''}`}
                  title="Code Block"
                >
                  <Code className="w-4 h-4" />
                </button>
              </div>

              {/* Undo/Redo */}
              <div className="flex items-center gap-1 pl-2">
                <button
                  onClick={() => editor.chain().focus().undo().run()}
                  className="p-2 rounded hover:bg-gray-200"
                  title="Undo"
                >
                  <Undo className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().redo().run()}
                  className="p-2 rounded hover:bg-gray-200"
                  title="Redo"
                >
                  <Redo className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Editor Content */}
            <div className="min-h-[400px] p-4 prose prose-lg max-w-none">
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Post Settings */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-gray-900">Post Settings</h3>
            
            {/* Author */}
            <div>
              <Label htmlFor="author">Author</Label>
              <Input
                id="author"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="Author name"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  placeholder="Add tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddTag}
                  variant="outline"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Featured Image */}
            <div>
              <Label>Featured Image</Label>
              <div className="mt-2 space-y-2">
                {formData.og_image_url ? (
                  <div className="relative">
                    <img 
                      src={formData.og_image_url} 
                      alt="Featured" 
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600 mb-2">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploadingImage}
                  className="hidden"
                  id="image-upload"
                />
                <Label 
                  htmlFor="image-upload" 
                  className="cursor-pointer"
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={isUploadingImage}
                    asChild
                  >
                    <span>
                      {isUploadingImage ? 'Uploading...' : formData.og_image_url ? 'Change Image' : 'Upload Image'}
                    </span>
                  </Button>
                </Label>
              </div>
            </div>

            {/* Featured */}
            <div className="flex items-center justify-between">
              <Label htmlFor="featured">Featured Post</Label>
              <Switch
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
            </div>

            {/* Published */}
            <div className="flex items-center justify-between">
              <Label htmlFor="published">Published</Label>
              <Switch
                id="published"
                checked={formData.published}
                onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
              />
            </div>
          </div>

          {/* SEO Settings */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-gray-900">SEO Settings</h3>
            
            <div>
              <Label htmlFor="meta_title">Meta Title</Label>
              <Input
                id="meta_title"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder="SEO title"
                maxLength={60}
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.meta_title?.length || 0}/60
              </p>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Description</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="SEO description"
                rows={3}
                maxLength={160}
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.meta_description?.length || 0}/160
              </p>
            </div>

            <div>
              <Label htmlFor="og_image">OG Image URL</Label>
              <Input
                id="og_image"
                value={formData.og_image_url}
                onChange={(e) => setFormData({ ...formData, og_image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {post?.id ? 'Update' : 'Create'} Post
                </>
              )}
            </Button>
            <Button
              onClick={onCancel}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
